import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CommandService } from '@helios/commands'
import { HeliosServerSideRequest } from '@helios/server-side-util'
import { RootState } from '@helios/state-management'
import {
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Store } from '@ngrx/store'
import { Observable, of } from 'rxjs'
import { catchError, map, mergeMap } from 'rxjs/operators'
import {
  OrigUnitsRequest,
  OrigUnitsResponse,
} from '../model/bulk-transaction/bulk-direct-transfer/bulk-direct-transfer.model'
import {
  BulkDerivedRequest,
  BulkDerivedResponse,
} from '../model/bulk-transaction/bulk-load/bulk-load.model'
import {
  CusipRequest,
  SecurityIdRequest,
} from '../model/bulk-transaction/bulk-misc-model'
import {
  BulkSecurityExchangeDerivedResponse,
  BulkSecurityExchangeDetailsRequest,
} from '../model/bulk-transaction/bulk-security-exchange/bulk-security-exchange.model'
import {
  BulkSecurityTransferDerivedRequest,
  BulkSecurityTransferDerivedResponse,
} from '../model/bulk-transaction/bulk-security-transfer/bulk-security-transfer.model'
import { SleeveSimple } from '../model/bulk-transaction/bulk-sleeve-simple-accounts.model'
import {
  AdditionalDetailsSecurityTransfer,
  BulkMvtResponse,
  ClearwaterTransactionDetailResponse,
} from '../model/bulk-transaction/bulk-transaction'
import {
  BulkEditTransactionNoteRequest,
  BulkTransactionRequest,
} from '../model/bulk-transaction/bulk-transaction.model'
import {
  Position,
  PositionsRequestContext,
} from '../model/bulk-transaction/bulk-transfer-out-all-positions-model'
import { BulkValidationView } from '../model/bulk-transaction/bulk-validation-view.model'
import { CounterParty } from '../model/counter-party.model'
import { Currency } from '../model/currency.model'
import { Basis, MvtBasis } from '../model/mvt-basis.model'
import {
  BulkPricingResponse,
  PricingRequest,
  PricingResponse,
} from '../model/pricing.model'
import { SecurityIdentifier } from '../model/security-identifier.model'
import { Security } from '../model/security.model'
import { AccountDetails } from '../model/single-transaction/account-details.model'
import { CashEntryTransactionDetails } from '../model/single-transaction/cash-entry/cash-entry-transaction-details'
import {
  CreateEditTransactionRequest,
  EditTransactionNoteRequest,
} from '../model/single-transaction/create-edit-transaction.model'
import {
  GetBalanceUnitsPayload,
  GetBalanceUnitsResponse,
} from '../model/single-transaction/direct-transfer/direct-transfer-transaction.model'
import { ExternalTransactionDetail } from '../model/single-transaction/external-transaction-detail'
import { ExternalUnmatchedTransaction } from '../model/single-transaction/external-unmatched-transaction'
import { LotInventoryConfig } from '../model/single-transaction/lot-inventory-config.model'
import {
  BulkMvtRequests,
  MarketValueTransferModel,
} from '../model/single-transaction/market-value-transfer.model'
import {
  CalculatedFactorRequest,
  CalculatedFactorResponse,
} from '../model/single-transaction/security-transfer/security-transfer.model'
import { SleeveSimpleAccounts } from '../model/single-transaction/sleeve-simple-accounts.model'
import {
  AmortDataTrade,
  AmortDataTradeRequest,
} from '../model/single-transaction/trade/amort-data-trade.model'
import { TranLink } from '../model/single-transaction/tran-link.model'
import { Transaction } from '../model/single-transaction/transaction'
import { TransactionMatch } from '../model/single-transaction/transaction-match'
import { TransactionNoteEdit } from '../model/single-transaction/transaction-note'
import { ValidationView } from '../model/single-transaction/validation-view.model'
import { TranCategoryUrl } from '../model/transaction-category.model'
import {
  BulkValidationResponse,
  ValidationInfo,
} from '../model/validation-response.model'
import { getTransactionCategoryFromTypeKey } from '../util/common-utils'
import { TransactionDetail } from './../model/single-transaction/transaction-detail'

interface GetPositionsResponse {
  data: Position[]
}

export const POSITIONS_URL = `helios-positions`
const TRANSACTIONS_HOST = 'helios-create-edit-trans'

const CURRENCY_URL = `${TRANSACTIONS_HOST}/security/currencies`
const COUNTER_PARTY_URL = `${TRANSACTIONS_HOST}/reconData/counterParties`
const TRANSACTION_MATCHES_URL = (transactionId: string) =>
  `${TRANSACTIONS_HOST}/transactionMatches/${transactionId}`
const EXTERNAL_TRANSACTION_SAVE_URL = `${TRANSACTIONS_HOST}/externalTransaction/`
const ACCOUNT_DETAIL_URL = `${TRANSACTIONS_HOST}/accountDetails/`
const FEE_TYPES_URL = `${TRANSACTIONS_HOST}/expenseAccrual/`
const PRICING_URL = `${TRANSACTIONS_HOST}/pricing/account/security/loadPricingInformation`
export const BULK_PRICING_URL = `${TRANSACTIONS_HOST}/pricing/account/security/loadBulkPricingInformation`
const SECURITY_PRICING_URL = `${TRANSACTIONS_HOST}/pricing/account/security/price/`
const MVT_URL = `${TRANSACTIONS_HOST}/transaction/mvt`
const BULK_BASIS_URL = `${TRANSACTIONS_HOST}/basis/bulk/info`
const BULK_COLLATERAL_BROKER_URL = `${TRANSACTIONS_HOST}/lots/bulk/isCollateralBroker`
const SECURITY_FROM_CUSIP_URL = `${TRANSACTIONS_HOST}/trade/securityData/cusip/`
const SECURITY_FROM_CUSIP_URL_NEW = `${TRANSACTIONS_HOST}/trade/securityData`
const SECURITY_FROM_SECID_URL = `${TRANSACTIONS_HOST}/trade/securityData/securityId/`
const CREATE_TRANSACTION_URL = `${TRANSACTIONS_HOST}`
const EDIT_TRANSACTION_URL = `${TRANSACTIONS_HOST}/transaction`
const BULK_CREATE_EDIT_TRANSACTION_URL = `${TRANSACTIONS_HOST}/{transaction-category}/transactions/bulk`
const TRANSACTION_DETAILS_URL_BULK = `${TRANSACTIONS_HOST}/{transaction-category}/transaction`
const TRANSACTION_DETAILS_URL_BULK_V2 = `${TRANSACTIONS_HOST}/{transaction-category}/v2/transaction`
export const EDIT_TRANSACTION_NOTE_URL = (transactionId: string) =>
  `${EDIT_TRANSACTION_URL}/${transactionId}/note`
const SECURITY_ID_FROM_CUSIP_URL = `${TRANSACTIONS_HOST}/securities/securityData`
export const LOT_INVENTORY_URL = `${TRANSACTIONS_HOST}/lots/inventoryConfig/account/`
const TRANSACTION_DETAILS_URL = (transactionCategory: string) =>
  `${TRANSACTIONS_HOST}/${transactionCategory}/transaction`
export const CASH_ENTRY_DETAILS_URL = (transactionId: string) =>
  `${TRANSACTIONS_HOST}/cashEntry/transaction/${transactionId}`
const TRANSACTION_VALIDATION_URL = `${TRANSACTIONS_HOST}/{transaction-category}/validate/`
const TRANSACTION_VALIDATION_URL_TRADE_CASH = `${TRANSACTIONS_HOST}/{transaction-category}/v3/validate/`
const BULK_TRANSACTION_VALIDATION_URL = `${TRANSACTIONS_HOST}/{transaction-category}/v2/validate`
const BULK_TRANSACTION_VALIDATION_URL_DIRECT_TRANSFER = `${TRANSACTIONS_HOST}/directTransfer/security/v2/validate`
const EXTERNAL_TRANSACTION_DETAIL_URL = `${TRANSACTIONS_HOST}/externalTransaction`
const EXTERNAL_TRANSACTION_DETAIL_URL_BULK = `${TRANSACTIONS_HOST}/externalTransaction/bulk`
const TRADE_AMORT_DATA_URL = `${TRANSACTIONS_HOST}/trade/amort/details/`
export const BALANCE_UNITS_URL = `${TRANSACTIONS_HOST}/directTransfer/balanceUnits/v2`
const TRAN_LINK_INFO_URL = `${TRANSACTIONS_HOST}/transaction/{transactionId}/links`
const AGGREGATE_SUBACCOUNTS_URL = `${TRANSACTIONS_HOST}/subAccounts/`
const BULK_AGGREGATE_SUBACCOUNTS_URL = `${TRANSACTIONS_HOST}/subAccounts/accountIds`
const BULK_ACCOUNT_TYPE_URL = `${TRANSACTIONS_HOST}/accountDetails/accountIds`
const BULK_TRANSFER_ADDITIONAL_DETAILS_URL = `${TRANSACTIONS_HOST}/securityTransfer/bulk/transactions/additionalDetails`
const BULK_TRANSFER_DERIVED_FIELDS_URL = `${TRANSACTIONS_HOST}/securityTransfer/bulk/transactions/derivedFields`
const BULK_TRANSFER_ALL_BASIS = `${TRANSACTIONS_HOST}/basis`
const BULK_TRANSFER_BASIS_DERIVED_FIELDS_URL = `${TRANSACTIONS_HOST}/securityTransfer/bulk/transactions/basisDerivedFields`
const MVT_URL_BULK = `${TRANSACTIONS_HOST}/directTransfer/mvt/bulk`
const BULK_EXCHANGE_DERIVED_FIELDS_URL = `${TRANSACTIONS_HOST}/directTransfer/security/bulk/exchange/details`
const BULK_EDIT_TRANSACTION_NOTE = `${TRANSACTIONS_HOST}/transaction/updateNotes`
const BULK_TRANSFER_DETAILS = `${TRANSACTIONS_HOST}/directTransfer/security/bulk/transfer/details`
const isHPLAccounts = `${TRANSACTIONS_HOST}/isHPLAccounts`
const GET_FACTOR = `${TRANSACTIONS_HOST}/securityTransfer/factor`

const PANEL_NAME = 'CreateEditTransaction'
enum TransactionActions {
  Create = 'create',
  Edit = 'edit',
  SaveExternalMatch = 'saveExternalMatch',
  EditNote = 'editNote',
  DeleteNote = 'deleteNote',
}

@Injectable({
  providedIn: 'root',
})
export class CreateEditTransactionService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly commandService: CommandService,
    private readonly store: Store<RootState>,
  ) {}

  loadCurrencies(): Observable<Currency[]> {
    return this.httpClient.get<Currency[]>(CURRENCY_URL, {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    })
  }

  loadCounterParties(): Observable<CounterParty[]> {
    return this.httpClient.get<CounterParty[]>(COUNTER_PARTY_URL)
  }

  loadTransactionDetail(
    sessionID: string,
    transactionId: string,
    requestParams: HttpParams,
    transCategoryUrl: string,
  ): Observable<TransactionDetail> {
    const options = {
      headers: new HttpHeaders().set('helios-session-id', sessionID),
      params: requestParams,
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.httpClient.get<TransactionDetail>(
      `${TRANSACTION_DETAILS_URL(transCategoryUrl)}/${transactionId}`,
      options,
    )
  }

  loadAllPositionsForTransferOut(
    request: HeliosServerSideRequest<Position, PositionsRequestContext>,
  ): Observable<Position[]> {
    return this.httpClient
      .post<GetPositionsResponse>(POSITIONS_URL, request)
      .pipe(map(response => response.data ?? []))
  }

  loadCashEntryTransactionDetails(
    transactionId: string,
    accountId: number,
    securityId: number,
  ): Observable<CashEntryTransactionDetails> {
    const params = new HttpParams()
      .set('accountId', accountId)
      .set('securityId', securityId)
    return this.httpClient.get<CashEntryTransactionDetails>(
      CASH_ENTRY_DETAILS_URL(transactionId),
      { params, context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  validateTransaction(
    transactionViewData: ValidationView,
    transCategoryUrl: string,
  ): Observable<ValidationInfo[]> {
    return this.httpClient.post<ValidationInfo[]>(
      TRANSACTION_VALIDATION_URL.replace(
        '{transaction-category}',
        transCategoryUrl,
      ),
      transactionViewData,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  validateTransactionTradeCash(
    transactionViewData: ValidationView,
    transCategoryUrl: TranCategoryUrl.CASH_ENTRY | TranCategoryUrl.TRADE_ENTRY,
  ): Observable<ValidationInfo[]> {
    return this.httpClient.post<ValidationInfo[]>(
      TRANSACTION_VALIDATION_URL_TRADE_CASH.replace(
        '{transaction-category}',
        transCategoryUrl,
      ),
      transactionViewData,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadTransactionMatches(
    transactionId: string,
  ): Observable<Map<string, TransactionMatch[]>> {
    return this.httpClient
      .get<{ [s: string]: TransactionMatch[] }>(
        TRANSACTION_MATCHES_URL(transactionId),
        { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
      )
      .pipe(map(matches => new Map(Object.entries(matches))))
  }

  loadExternalTransactionDetail(
    extTranId: number,
    accountId: number,
  ): Observable<ExternalTransactionDetail> {
    return this.httpClient.get<ExternalTransactionDetail>(
      `${EXTERNAL_TRANSACTION_DETAIL_URL}/${extTranId}/${accountId}`,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadAccountDetails(accountId: number): Observable<AccountDetails> {
    return this.httpClient.get<AccountDetails>(ACCOUNT_DETAIL_URL + accountId, {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    })
  }

  getBalanceUnits(
    getBalanceUnitsPayload: GetBalanceUnitsPayload,
  ): Observable<GetBalanceUnitsResponse> {
    const options = {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.httpClient.post<GetBalanceUnitsResponse>(
      BALANCE_UNITS_URL,
      getBalanceUnitsPayload,
      options,
    )
  }

  loadFeeAccrual(accountId: number): Observable<{ [s: string]: string }> {
    return this.httpClient.get<{ [s: string]: string }>(
      FEE_TYPES_URL + accountId,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadMvtValues(mvtData: MarketValueTransferModel): Observable<MvtBasis> {
    return this.httpClient.post<MvtBasis>(MVT_URL, mvtData, {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    })
  }

  loadInventoryConfig(accountId: number): Observable<LotInventoryConfig> {
    return this.httpClient.get<LotInventoryConfig>(
      LOT_INVENTORY_URL + accountId,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadBasisForAccounts(
    accountIds: number[],
  ): Observable<{ [key: number]: Basis[] }> {
    return this.httpClient.post<{ [key: number]: Basis[] }>(
      BULK_BASIS_URL,
      accountIds,
    )
  }

  isCollateralBroker(
    accountIds: number[],
  ): Observable<{ [key: number]: boolean }> {
    return this.httpClient.post<{ [key: number]: boolean }>(
      BULK_COLLATERAL_BROKER_URL,
      accountIds,
    )
  }

  fetchSecurityDetailsFromCusip(
    cusip: string,
    settleDate: string,
    accountId: number,
  ): Observable<Security> {
    const options = {
      params: new HttpParams()
        .append('settleDate', settleDate)
        .append('cusip', cusip)
        .append('accountId', accountId),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.httpClient.get<Security>(SECURITY_FROM_CUSIP_URL_NEW, options)
  }

  fetchSecurityDetailsFromCusips(
    cusipRequest: CusipRequest[],
  ): Observable<{ [key: string]: Security }> {
    return this.httpClient
      .post<{ [key: string]: Security }>(SECURITY_FROM_CUSIP_URL, cusipRequest)
      .pipe(
        catchError(error => {
          return of(error)
        }),
      )
  }

  /**
   * getIsHplAccounts for a account ids in bulk
   *
   * @internal
   */

  getIsHplAccounts(
    accountIds: number[],
  ): Observable<{ [key: string]: boolean }> {
    return this.httpClient
      .post<{ [key: string]: boolean }>(isHPLAccounts, accountIds)
      .pipe(
        catchError(error => {
          return of(error)
        }),
      )
  }

  fetchSecurityDetailsFromSecurityId(
    securityId: number,
    settleDate: string,
    accountId: number,
  ): Observable<Security> {
    const options = {
      params: new HttpParams()
        .append('settleDate', settleDate)
        .append('accountId', accountId),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.httpClient.get<Security>(
      SECURITY_FROM_SECID_URL + securityId,
      options,
    )
  }

  fetchSecurityDetailsFromSecurityIds(
    securityIdRequest: SecurityIdRequest[],
  ): Observable<{ [key: string]: Security }> {
    return this.httpClient
      .post<Security>(SECURITY_FROM_SECID_URL, securityIdRequest)
      .pipe(
        catchError(error => {
          return of(error)
        }),
      )
  }

  fetchSecurityIdFromCusip(
    cusipList: string[],
  ): Observable<SecurityIdentifier[]> {
    return this.httpClient.post<SecurityIdentifier[]>(
      SECURITY_ID_FROM_CUSIP_URL,
      {
        cusip: cusipList,
      },
      {
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  loadPricingInformation(
    pricingRequest: PricingRequest,
  ): Observable<PricingResponse> {
    return this.httpClient.post<PricingResponse>(PRICING_URL, pricingRequest, {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    })
  }

  loadBulkPricingInformation(
    pricingRequest: PricingRequest[],
  ): Observable<BulkPricingResponse> {
    return this.httpClient.post<BulkPricingResponse>(
      BULK_PRICING_URL,
      pricingRequest,
      {
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  loadSecurityPrice(
    accountId: number,
    cusip: string,
    transferDate: string,
    sessionId: string,
  ): Observable<PricingResponse> {
    const options = {
      headers: new HttpHeaders().set('helios-session-id', sessionId),
      params: new HttpParams()
        .set('accountId', accountId.toString())
        .set('cusip', cusip)
        .set('date', transferDate),
    }
    return this.httpClient.get<PricingResponse>(SECURITY_PRICING_URL, options)
  }

  createTransaction(
    sessionId: string,
    createEditTransactionRequest: CreateEditTransactionRequest,
  ): Observable<string> {
    return this.httpClient.post(
      CREATE_TRANSACTION_URL,
      createEditTransactionRequest,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          TransactionActions.Create,
        ),
        responseType: 'text',
        context: new HttpContext()
          .set(HELIOS_COMMAND_HTTP_TOKEN, true)
          .set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  editTransaction(
    sessionId: string,
    transactionId: string,
    createEditTransactionRequest: CreateEditTransactionRequest,
  ): Observable<string> {
    return this.httpClient.put(
      `${EDIT_TRANSACTION_URL}/${transactionId}`,
      createEditTransactionRequest,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          TransactionActions.Edit,
        ),
        responseType: 'text',
        context: new HttpContext()
          .set(HELIOS_COMMAND_HTTP_TOKEN, true)
          .set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  saveExternalMatch(
    sessionId: string,
    createEditTransactionRequest: CreateEditTransactionRequest,
  ): Observable<string> {
    return this.httpClient.post(
      EXTERNAL_TRANSACTION_SAVE_URL,
      createEditTransactionRequest,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          TransactionActions.SaveExternalMatch,
        ),
        responseType: 'text',
        context: new HttpContext()
          .set(HELIOS_COMMAND_HTTP_TOKEN, true)
          .set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  fetchTranLinkInfo(transactionId: string): Observable<TranLink> {
    return this.httpClient.get<TranLink>(
      TRAN_LINK_INFO_URL.replace('{transactionId}', transactionId),
    )
  }

  loadAmortDataForTrade(
    amortDataTradeRequest: AmortDataTradeRequest,
  ): Observable<AmortDataTrade> {
    return this.httpClient.post<AmortDataTrade>(
      TRADE_AMORT_DATA_URL,
      amortDataTradeRequest,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  editNote(
    transactionId: string,
    edit: TransactionNoteEdit,
    isDelete = false,
  ): Observable<void> {
    return this.httpClient
      .get<Transaction>(`${TRANSACTIONS_HOST}/transaction/${transactionId}`)
      .pipe(
        mergeMap(transaction => {
          const tranCategory = getTransactionCategoryFromTypeKey(
            transaction.tranType,
          )
          const request: EditTransactionNoteRequest = {
            notes: isDelete
              ? 'Delete Transaction Note'
              : 'Edit Transaction Note',
            tranCategory: tranCategory,
            transaction,
            transactionNote: edit,
          }
          return this.httpClient.put<void>(
            EDIT_TRANSACTION_NOTE_URL(transactionId),
            request,
            {
              headers: this.commandService.generateTemporaryHeaders(
                PANEL_NAME,
                isDelete
                  ? TransactionActions.DeleteNote
                  : TransactionActions.EditNote,
              ),
              context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
            },
          )
        }),
      )
  }

  fetchSubAccounts(accountId: number): Observable<SleeveSimpleAccounts[]> {
    return this.httpClient.get<SleeveSimpleAccounts[]>(
      `${AGGREGATE_SUBACCOUNTS_URL}` + accountId,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  validateBulkTransaction(
    transactionViewData: BulkValidationView,
    transCategoryUrl: string,
  ): Observable<BulkValidationResponse> {
    return this.httpClient.post<BulkValidationResponse>(
      BULK_TRANSACTION_VALIDATION_URL.replace(
        '{transaction-category}',
        transCategoryUrl,
      ),
      transactionViewData,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  validateBulkTransactionDirectTransfer(
    transactionViewData: BulkValidationView,
  ): Observable<BulkValidationResponse> {
    return this.httpClient.post<BulkValidationResponse>(
      BULK_TRANSACTION_VALIDATION_URL_DIRECT_TRANSFER,
      transactionViewData,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  createBulkTransaction(
    createEditTransactionRequest: BulkTransactionRequest,
    transCategoryUrl: string,
  ): Observable<string> {
    return this.httpClient.post(
      BULK_CREATE_EDIT_TRANSACTION_URL.replace(
        '{transaction-category}',
        transCategoryUrl,
      ),
      createEditTransactionRequest,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          TransactionActions.Create,
        ),
        responseType: 'text',
        context: new HttpContext()
          .set(HELIOS_COMMAND_HTTP_TOKEN, true)
          .set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  editBulkTransaction(
    createEditTransactionRequest: BulkTransactionRequest,
    transCategoryUrl: string,
  ): Observable<string> {
    return this.httpClient.put(
      BULK_CREATE_EDIT_TRANSACTION_URL.replace(
        '{transaction-category}',
        transCategoryUrl,
      ),
      createEditTransactionRequest,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          TransactionActions.Edit,
        ),
        responseType: 'text',
        context: new HttpContext()
          .set(HELIOS_COMMAND_HTTP_TOKEN, true)
          .set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  loadTransactionDetailBulk(
    transactionIds: string[],
    securityIds: number[] | undefined,
    transCategoryUrl: string,
    isTransferInAndOut = false,
  ): Observable<ClearwaterTransactionDetailResponse> {
    let postParam: unknown = { transactionIds, securityIds }
    if (transCategoryUrl == TranCategoryUrl.FX_TRADE) {
      postParam = { transactionIds }
    }
    if (transCategoryUrl == TranCategoryUrl.TRANSFER) {
      postParam = { transactionIds, securityIds, isTransferInAndOut }
    }
    return this.httpClient.post<ClearwaterTransactionDetailResponse>(
      TRANSACTION_DETAILS_URL_BULK.replace(
        '{transaction-category}',
        transCategoryUrl,
      ),
      postParam,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadTransactionDetailBulkFxTrade(
    transactionIds: string[],
  ): Observable<ClearwaterTransactionDetailResponse> {
    return this.httpClient.post<ClearwaterTransactionDetailResponse>(
      TRANSACTION_DETAILS_URL_BULK.replace(
        '{transaction-category}',
        TranCategoryUrl.FX_TRADE,
      ),
      { transactionIds },
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadTransactionDetailBulkV2(
    transactionIds: string[],
    securityData: SecurityIdRequest[] | undefined,
    transCategoryUrl: string,
  ): Observable<ClearwaterTransactionDetailResponse> {
    return this.httpClient.post<ClearwaterTransactionDetailResponse>(
      TRANSACTION_DETAILS_URL_BULK_V2.replace(
        '{transaction-category}',
        transCategoryUrl,
      ),
      { transactionIds, securityData },
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadAdditionalDetailsTransferBulk(
    legacyOrigIds: number[],
    legacyTranIds: number[],
    securityIds: number[] | undefined,
    accountIds: number[],
    isTransferInAndOut = false,
  ): Observable<AdditionalDetailsSecurityTransfer> {
    return this.httpClient.post<AdditionalDetailsSecurityTransfer>(
      BULK_TRANSFER_ADDITIONAL_DETAILS_URL,
      {
        legacyOrigIds,
        legacyTranIds,
        securityIds,
        accountIds,
        isTransferInAndOut,
      },
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }
  loadExtTransactionDetailBulk(
    tranId: number[],
    sessionAccountId: number | undefined,
  ): Observable<ExternalUnmatchedTransaction[]> {
    // Guard the scope: a missing accountId would stringify to "accountId=undefined"
    // and send a wrong-scope lookup to the backend. Abort instead.
    if (sessionAccountId == null) {
      throw new Error(
        'loadExtTransactionDetailBulk requires a loaded session account',
      )
    }
    return this.httpClient.post<ExternalUnmatchedTransaction[]>(
      EXTERNAL_TRANSACTION_DETAIL_URL_BULK,
      tranId,
      {
        params: new HttpParams().append('accountId', sessionAccountId),
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }

  fetchAllSubAccounts(
    accountIds: number[],
  ): Observable<{ [s: number]: SleeveSimple[] }> {
    return this.httpClient.post<{ [s: number]: SleeveSimple[] }>(
      `${BULK_AGGREGATE_SUBACCOUNTS_URL}`,
      accountIds,
    )
  }

  fetchAccountTypes(accountIds: number[]): Observable<{ [s: number]: string }> {
    return this.httpClient.post<{ [s: number]: string }>(
      `${BULK_ACCOUNT_TYPE_URL}`,
      accountIds,
    )
  }

  getDerivedFieldsForBulkLoad(
    bulkDerivedRequest: BulkDerivedRequest,
  ): Observable<{ [key: string]: BulkDerivedResponse }> {
    return this.httpClient.post<{ [key: string]: BulkDerivedResponse }>(
      BULK_TRANSFER_DERIVED_FIELDS_URL,
      bulkDerivedRequest,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }
  fetchAllBasis(): Observable<Basis[]> {
    return this.httpClient.get<Basis[]>(`${BULK_TRANSFER_ALL_BASIS}`, {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    })
  }

  getDerivedFieldsForSecurityTransfer(
    bulkDerivedRequest: BulkSecurityTransferDerivedRequest,
  ): Observable<BulkSecurityTransferDerivedResponse> {
    return this.httpClient.post<BulkSecurityTransferDerivedResponse>(
      BULK_TRANSFER_BASIS_DERIVED_FIELDS_URL,
      bulkDerivedRequest,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  loadBulkMvtValues(mvtData: BulkMvtRequests): Observable<BulkMvtResponse> {
    return this.httpClient.post<BulkMvtResponse>(MVT_URL_BULK, mvtData, {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    })
  }

  editNoteBulk(
    trans: unknown,
    commandType: string,
    transactionNote: string,
    isDelete = false,
  ): Observable<void> {
    const request: BulkEditTransactionNoteRequest = {
      notes: isDelete ? 'Delete Transaction Note' : 'Edit Transaction Note',
      transaction: trans,
      transactionNote: transactionNote,
      commandType: commandType,
    }
    return this.httpClient.put<void>(BULK_EDIT_TRANSACTION_NOTE, request, {
      headers: this.commandService.generateTemporaryHeaders(
        PANEL_NAME,
        isDelete ? TransactionActions.DeleteNote : TransactionActions.EditNote,
      ),
      context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
    })
  }

  getTransferDetails(
    transferDetailsRequests: OrigUnitsRequest[],
  ): Observable<OrigUnitsResponse> {
    const payload = {
      transferDetailsRequests: transferDetailsRequests,
    }
    return this.httpClient.post<OrigUnitsResponse>(
      `${BULK_TRANSFER_DETAILS}`,
      payload,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  getDerivedFieldsForSecurityExchange(
    bulkDerivedRequest: BulkSecurityExchangeDetailsRequest,
  ): Observable<BulkSecurityExchangeDerivedResponse> {
    return this.httpClient.post<BulkSecurityExchangeDerivedResponse>(
      BULK_EXCHANGE_DERIVED_FIELDS_URL,
      bulkDerivedRequest,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  getCalculatedFactor(
    securityId: number,
    request: CalculatedFactorRequest,
    accountId: number,
  ): Observable<CalculatedFactorResponse> {
    return this.httpClient.post<CalculatedFactorResponse>(
      `${GET_FACTOR}/${securityId}`,
      request,
      {
        params: new HttpParams().append('accountId', accountId),
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }
}
